import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Box, Cpu, Zap, Shield, Target, Code2, Sparkles, Workflow, Landmark } from "lucide-react";

const VALUES = [
  { n: "01", icon: Box, title: "Modularity", desc: "Systems built to adapt, expand, and reconfigure as your business shifts. No lock-in." },
  { n: "02", icon: Cpu, title: "Precision", desc: "Fine-tuned code, zero bloat. Weighted, tactile, intentional." },
  { n: "03", icon: Zap, title: "Speed", desc: "Sub-1s execution and rapid builds. Every millisecond audited." },
  { n: "04", icon: Shield, title: "Sovereignty", desc: "Your data, your code, 100% owned by you. Local models, no leakage." },
];

const TEAMS = [
  { color: "#B91C2C", accent: "text-[#FF3355]", ring: "border-[#B91C2C]/50", tag: "RED",   icon: Target,   title: "Red Team — Growth & Outbound",       desc: "Engineering high-converting lead pipelines, outbound infiltration, and direct-response conversion strategies." },
  { color: "#10B981", accent: "text-[#34D399]", ring: "border-[#10B981]/50", tag: "GREEN", icon: Code2,    title: "Green Team — Web & Software Architecture", desc: "Sub-1-second Next.js/Webflow digital flagships, custom databases, and technical SEO frameworks." },
  { color: "#6366F1", accent: "text-[#818CF8]", ring: "border-[#6366F1]/50", tag: "BLUE",  icon: Sparkles, title: "Blue Team — Creative & AI Media",     desc: "Bespoke UI/UX design systems, short-form AI video production (LTX-2, Wan 2.1, Veo 3), and brand assets." },
  { color: "#F59E0B", accent: "text-[#FBBF24]", ring: "border-[#F59E0B]/50", tag: "GOLD",  icon: Workflow, title: "Gold Team — Automation & MLOps",      desc: "Local AI nodes, API integrations, and TraceElements OS workflow automation." },
];

const PILLARS = [
  { title: "True Freedom Through Modularity", body: "No two businesses or creative workflows are identical. We don't force you into rigid, one-size-fits-all software templates. Every system we deploy — from our TraceElements OS business engine to our local LLM vector databases — is built modularly, allowing you to adapt, expand, or reconfigure as your needs evolve." },
  { title: "Tactile Precision & High Craftsmanship", body: "Just like precision-milled hardware, software should feel responsive, weighted, and intentional. We build sub-1-second web platforms, clean API architectures, and structured JSON-LD entity schema with meticulous attention to detail." },
  { title: "AI Acceleration Without the Noise", body: "We leverage generative AI and custom agent workflows not to replace human craft, but to supercharge it. By automating boilerplate code, routine lead management, and repetitive video rendering, we allow founders and creators to focus entirely on strategy, story, and growth." },
  { title: "Absolute Data Sovereignty", body: "Your proprietary data, client lists, and creative assets should belong to you. We champion local models, air-gapped architectures, and open-source stacks that protect your IP with zero third-party cloud data leakage and $0 in recurring per-token fees." },
];

const fade = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };

export default function AboutPage() {
  return (
    <div className="bg-[#0B0C10] text-[#F5F5F7]" data-testid="about-page">
      {/* HERO */}
      <section className="relative border-b border-[#1F2833] py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 te-grid-bg-dense opacity-30" />
        <div className="absolute -top-40 -right-20 w-[520px] h-[520px] bg-[#B91C2C]/10 blur-[140px] rounded-full" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">// ABOUT.TRACE.ELEMENTS</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-5xl">
              Re-imagining Digital Infrastructure &<br className="hidden lg:block" />
              <span className="text-[#FF3355]">Creative Control.</span>
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-[#C5C6C7] max-w-3xl leading-relaxed">
              At TraceElements, we believe technology should feel like an extension of your mind — not a bottleneck.
              Whether we're engineering custom web platforms, deploying air-gapped private AI nodes, or optimizing local
              search and media pipelines, our mission is simple: eliminate digital friction, restore creative flow, and
              build high-performance systems tailored to the way you work.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ORIGINS */}
      <section className="border-b border-[#1F2833] py-20 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">// 01 · OUR.ORIGINS</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter leading-tight">
              A studio built to <span className="text-[#FF3355]">unblock builders.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6 text-[#C5C6C7] leading-relaxed">
            <p>
              We started with a simple observation: <span className="text-[#F5F5F7] font-medium">modern software and digital tools
              are powerful, but the way we interact with them is broken.</span>
            </p>
            <p>
              Creators, founders, and enterprise teams spend hours trapped behind rigid templates, bloated agency retainers,
              fragmented subscription tools, and slow, repetitive workflows.
            </p>
            <p>
              TraceElements was founded to change that. Based out of <span className="text-[#FF3355]">Memphis, Tennessee</span>,
              we combined high-speed software engineering, custom hardware control logic, and advanced AI agents to build a new
              kind of technical studio. We replaced slow agency hours and generic "AI slop" with precise, modular, and automated
              digital architecture.
            </p>
          </div>
        </div>
      </section>

      {/* CORE VALUES GRID */}
      <section className="border-b border-[#1F2833] py-20 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 te-grid-bg-dense opacity-20" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">// 02 · CORE.VALUES</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter">The TraceElements operating stance.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1F2833] border border-[#1F2833]">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="bg-[#0B0C10] p-8 group hover:bg-[#111319] transition-colors"
                  data-testid={`about-value-${v.n}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF3355]">{v.n}</span>
                    <Icon size={18} className="text-[#FF3355]" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-[#F5F5F7] group-hover:text-[#FF3355] transition-colors">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#C5C6C7] leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY / PILLARS */}
      <section className="border-b border-[#1F2833] py-20 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">// 03 · THE.PHILOSOPHY</div>
              <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter max-w-2xl">How we work.</h2>
            </div>
            <p className="text-[#C5C6C7] max-w-md">Four principles that govern every engagement — from a landing-page rebuild to an on-prem LLM deployment.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F2833] border border-[#1F2833]">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: (i % 2) * 0.08 }}
                className="bg-[#0B0C10] p-8 lg:p-10"
                data-testid={`about-pillar-${i}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF3355] mb-4">// PILLAR {String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">{p.title}</h3>
                <p className="mt-4 text-[#C5C6C7] leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUR TEAMS */}
      <section className="border-b border-[#1F2833] py-20 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 te-grid-bg-dense opacity-20" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">// 04 · THE.ENGINE</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter max-w-3xl">Four specialized divisions. One accountable execution layer.</h2>
            <p className="mt-6 text-[#C5C6C7] max-w-3xl">
              To deliver seamless technical execution, TraceElements operates through four dedicated internal core teams.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TEAMS.map((t, i) => {
              const Icon = t.icon;
              return (
                <motion.div
                  key={t.tag}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className={`relative border ${t.ring} bg-[#0B0C10] p-8 lg:p-10 group hover:bg-[#111319] transition-colors`}
                  data-testid={`about-team-${t.tag.toLowerCase()}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-mono text-[10px] uppercase tracking-[0.28em] ${t.accent}`}>// {t.tag}.TEAM</span>
                    <Icon size={20} style={{ color: t.color }} />
                  </div>
                  <h3 className="mt-6 font-display text-xl lg:text-2xl font-bold tracking-tight">{t.title}</h3>
                  <p className="mt-3 text-sm text-[#C5C6C7] leading-relaxed">{t.desc}</p>
                  <div className="mt-6 h-[2px]" style={{ background: `linear-gradient(90deg, ${t.color}, transparent)` }} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXECUTIVE LEADERSHIP */}
      <section className="border-b border-[#1F2833] py-20 lg:py-24 relative overflow-hidden">
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">// 05 · EXECUTIVE.LEADERSHIP</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter max-w-3xl">Accountability at the top.</h2>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="relative border border-[#B91C2C]/50 bg-[#0B0C10] p-8 lg:p-10 max-w-2xl hover:bg-[#111319] transition-colors"
            data-testid="about-leader-cfo"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF3355]">// CHIEF.FINANCIAL.OFFICER</span>
              <Landmark size={20} className="text-[#B91C2C]" />
            </div>
            <div className="mt-6 flex items-center gap-5">
              <span className="h-16 w-16 shrink-0 rounded-full border border-[#B91C2C]/60 bg-[#B91C2C]/10 flex items-center justify-center font-display text-xl font-bold text-[#FF3355]">DW</span>
              <div>
                <h3 className="font-display text-xl lg:text-2xl font-bold tracking-tight">Diane Weithers <span className="text-[#C5C6C7] text-base font-medium">MBA, BSN</span></h3>
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C5C6C7] mt-1">Chief Financial Officer</div>
              </div>
            </div>
            <p className="mt-5 text-sm text-[#C5C6C7] leading-relaxed">
              Directs capital strategy, financial governance, and the operational discipline behind every TraceElements deployment —
              blending an MBA's financial rigor with a clinician's precision for process.
            </p>
            <div className="mt-6 h-[2px]" style={{ background: "linear-gradient(90deg, #B91C2C, transparent)" }} />
          </motion.div>
        </div>
      </section>

      {/* COMMITMENT + CTA */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute -bottom-40 -left-20 w-[520px] h-[520px] bg-[#B91C2C]/10 blur-[140px] rounded-full" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">// 06 · OUR.COMMITMENT</div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter max-w-4xl">
            We stand by the quality of our code and <span className="text-[#FF3355]">the clarity of our systems.</span>
          </h2>
          <p className="mt-6 text-lg text-[#C5C6C7] max-w-3xl leading-relaxed">
            Whether you're an emerging founder launching your first digital flagship or an enterprise firm securing your
            private AI data, TraceElements gives you the tools, speed, and precision to dominate your space.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/services"
              data-testid="about-cta-services"
              className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors"
            >
              [ Explore Our Services ] <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/terminal"
              data-testid="about-cta-terminal"
              className="inline-flex items-center gap-2 border border-[#1F2833] hover:border-[#FF3355] text-[#F5F5F7] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em]"
            >
              Talk to an Infrastructure Architect <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
